import 'chartjs-adapter-date-fns'
import { lazy, Suspense, useEffect } from 'react'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

import { AccoladesApi, AccountsApi, SeasonsApi, TeamsApi } from './clients'
import { getConfiguration } from './clients/configuration'

import { authenticate } from './redux/features/auth/authSlice'
import { setCurrentUser } from './redux/features/currentUser/currentUserSlice'
import { setSeasons } from './redux/features/seasons/seasonsSlice'
import { setTeams } from './redux/features/teams/teamsSlice'
import { useAppDispatch, useAppSelector } from './redux/hooks'

import { ConfirmAccount } from './pages/auth/ConfirmAccount'
import { CreateAccount } from './pages/auth/CreateAccount'
import { ForgotPassword } from './pages/auth/ForgotPassword'
import { LoginCallback } from './pages/auth/LoginCallback'
import { ResetPassword } from './pages/auth/ResetPassword'
import { SignIn } from './pages/auth/SignIn'
import { EmailUnsubscribe } from './pages/EmailUnsubscribe'
import { Home } from './pages/Home'

import { NoAuthRoute } from './components/NoAuthRoute/NoAuthRoute'
import { SecureRoute } from './components/SecureRoute/SecureRoute'
import { SuperadminRoute } from './components/SecureRoute/SuperadminRoute'
import { Wrapper } from './components/Wrapper/Wrapper'

import { setAccolades } from './redux/features/accolades/accoladesSlice'
import { getAuthService } from './services/authService'

// Loading fallback component
const LoadingFallback = () => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh'
        }}
    >
        <CircularProgress />
    </Box>
)

export const App = () => {

    const dispatch = useAppDispatch()
    const authState = useAppSelector(state => state.auth.idTokenPayload)

    const PlayersMasterRoute = lazy(() => import('./pages/players/PlayersMaster').then(module => ({ default: module.PlayersMaster })))
    const PlayersDetailRoute = lazy(() => import('./pages/players/PlayersDetail').then(module => ({ default: module.PlayersDetail })))
    const TeamsMasterRoute = lazy(() => import('./pages/teams/TeamsMaster').then(module => ({ default: module.TeamsMaster })))
    const TeamsDetailRoute = lazy(() => import('./pages/teams/TeamsDetail').then(module => ({ default: module.TeamsDetail })))
    const MatchesMasterRoute = lazy(() => import('./pages/matches/MatchesMaster').then(module => ({ default: module.MatchesMaster })))
    const MatchesDetailRoute = lazy(() => import('./pages/matches/MatchesDetail').then(module => ({ default: module.MatchesDetail })))
    const LeaguesDashboardRoute = lazy(() => import('./pages/leagues/LeaguesDashboard').then(module => ({ default: module.LeaguesDashboard })))
    const CreateLeagueRoute = lazy(() => import('./pages/leagues/CreateLeague').then(module => ({ default: module.CreateLeague })))
    const JoinLeagueRoute = lazy(() => import('./pages/leagues/JoinLeague').then(module => ({ default: module.JoinLeague })))
    const JoinLeagueFromCodeRoute = lazy(() => import('./pages/leagues/JoinLeagueFromCode').then(module => ({ default: module.JoinLeagueFromCode })))
    const LeagueDetailRoute = lazy(() => import('./pages/leagues/detail/LeagueDetailWrapper').then(module => ({ default: module.LeagueDetailWrapper })))
    const LeagueDetailLeaderboardRoute = lazy(() => import('./pages/leagues/detail/LeagueDetailLeaderboard').then(module => ({ default: module.LeagueDetailLeaderboard })))
    const LeagueDetailMemberPicksRoute = lazy(() => import('./pages/leagues/detail/LeagueDetailMemberPicks').then(module => ({ default: module.LeagueDetailMemberPicks })))
    const LeagueDetailMembersRoute = lazy(() => import('./pages/leagues/detail/LeagueDetailMembers').then(module => ({ default: module.LeagueDetailMembers })))
    const PickAnalyticsRoute = lazy(() => import('./pages/leagues/detail/PickAnalytics').then(module => ({ default: module.PickAnalytics })))
    const LeagueDetailSettingsRoute = lazy(() => import('./pages/leagues/detail/admin/LeagueDetailSettings').then(module => ({ default: module.LeagueDetailSettings })))
    const LeagueDetailBracketManagementRoute = lazy(() => import('./pages/leagues/detail/admin/LeagueDetailBracketManagement').then(module => ({ default: module.LeagueDetailBracketManagement })))
    const LeagueDetailPeriodManagementRoute = lazy(() => import('./pages/leagues/detail/admin/LeagueDetailPeriodManagement').then(module => ({ default: module.LeagueDetailPeriodManagement })))
    const LeagueDetailPickConfigurationsRoute = lazy(() => import('./pages/leagues/detail/admin/LeagueDetailPickConfigurations').then(module => ({ default: module.LeagueDetailPickConfigurations })))
    const LeagueDetailMemberManagementRoute = lazy(() => import('./pages/leagues/detail/admin/LeagueDetailMemberManagement').then(module => ({ default: module.LeagueDetailMemberManagement })))
    const LeagueDetailModifyBracketRoute = lazy(() => import('./pages/leagues/detail/bracket/ModifyBracket').then(module => ({ default: module.ModifyBracket })))
    const LeagueDetailViewBracketRoute = lazy(() => import('./pages/leagues/detail/bracket/ViewBracket').then(module => ({ default: module.ViewBracket })))
    const LeagueDetailBracketPoolDashboardRoute = lazy(() => import('./pages/leagues/detail/bracket/LeagueBracketDashboard').then(module => ({ default: module.LeagueBracketDashboard })))
    const CreatePickConfigRoute = lazy(() => import('./pages/leagues/detail/admin/CreatePickConfig').then(module => ({ default: module.CreatePickConfig })))
    const EditMatchPickConfigRoute = lazy(() => import('./pages/leagues/detail/admin/EditMatchPickConfig').then(module => ({ default: module.EditMatchPickConfig })))
    const EditPeriodPickConfigRoute = lazy(() => import('./pages/leagues/detail/admin/EditPeriodPickConfig').then(module => ({ default: module.EditStatPickConfig })))
    const LeagueDetailBulkCreateMatchPickConfigRoute = lazy(() => import('./pages/leagues/detail/admin/LeagueDetailBulkCreateMatchPickConfig').then(module => ({ default: module.LeagueDetailBulkCreateMatchPickConfig })))
    const MyAccountRoute = lazy(() => import('./pages/MyAccount').then(module => ({ default: module.MyAccount })))
    const AdminPanelRoute = lazy(() => import('./pages/admin/AdminPanel').then(module => ({ default: module.AdminPanel })))
    const AdminToolsRoute = lazy(() => import('./pages/admin/tools/AdminTools').then(module => ({ default: module.AdminTools })))
    const AdminBracketsMasterRoute = lazy(() => import('./pages/admin/brackets/AdminBracketsMaster').then(module => ({ default: module.AdminBracketsMaster })))
    const AdminBracketDetailRoute = lazy(() => import('./pages/admin/brackets/AdminBracketDetail').then(module => ({ default: module.AdminBracketDetail })))
    const AdminSeasonsMasterRoute = lazy(() => import('./pages/admin/seasons/AdminSeasonsMaster').then(module => ({ default: module.AdminSeasonsMaster })))
    const AdminSeasonsDetailRoute = lazy(() => import('./pages/admin/seasons/AdminSeasonsDetail').then(module => ({ default: module.AdminSeasonsDetail })))
    const AdminTeamsMasterRoute = lazy(() => import('./pages/admin/teams/AdminTeamsMaster').then(module => ({ default: module.AdminTeamsMaster })))
    const AdminTeamsDetailRoute = lazy(() => import('./pages/admin/teams/AdminTeamsDetail').then(module => ({ default: module.AdminTeamsDetail })))
    const AdminTeamSeasonDetailRoute = lazy(() => import('./pages/admin/teams/AdminTeamSeasonDetail').then(module => ({ default: module.AdminTeamSeasonDetail })))
    const AdminPlayersMasterRoute = lazy(() => import('./pages/admin/players/AdminPlayersMaster').then(module => ({ default: module.AdminPlayersMaster })))
    const AdminPlayersDetailRoute = lazy(() => import('./pages/admin/players/AdminPlayersDetail').then(module => ({ default: module.AdminPlayersDetail })))
    const AdminMatchesMasterRoute = lazy(() => import('./pages/admin/matches/AdminMatchesMaster').then(module => ({ default: module.AdminMatchesMaster })))
    const AdminMatchesDetailRoute = lazy(() => import('./pages/admin/matches/AdminMatchesDetail').then(module => ({ default: module.AdminMatchesDetail })))
    const AdminAccountsMasterRoute = lazy(() => import('./pages/admin/accounts/AdminAccountsMaster').then(module => ({ default: module.AdminAccountsMaster })))
    const AdminAccoladesMasterRoute = lazy(() => import('./pages/admin/accolades/AdminAccoladesMaster').then(module => ({ default: module.AdminAccoladesMaster })))
    const AttendanceRoute = lazy(() => import('./pages/Attendance').then(module => ({ default: module.Attendance })))

    useEffect(() => {
        const auth = getAuthService()
        auth.onTokensUpdated((tokens) => {
            dispatch(authenticate(tokens))
        })
        auth.authenticateSilently().then(res => {
            if (res.accessToken) {
                dispatch(authenticate({
                    accessToken: res.accessToken,
                    idToken: res.idToken,
                    refreshToken: res.refreshToken,
                    expiresIn: 0,
                    tokenType: 'Bearer',
                    newDeviceMetadata: res.newDeviceMetadata
                }))
            } else {
                dispatch(authenticate(null))
            }
        }).catch(() => {
            auth.logout()
        })
    }, [dispatch])

    useEffect(() => {
        const getCurrentUser = async (id: string) => {
            const config = await getConfiguration()
            const accountsApi = new AccountsApi(config)
            const account = await accountsApi.getAccount({ id })
            return account.account
        }
        if (authState) {
            getCurrentUser(authState.sub)
                .then(acc => {
                    dispatch(setCurrentUser({
                        ...acc,
                        createdAt: acc.createdAt.toISOString(),
                        updatedAt: acc.updatedAt.toISOString(),
                        lastSeenDate: acc.lastSeenDate.toISOString(),
                    }))
                })
        } else {
            dispatch(setCurrentUser(null))
        }
    }, [dispatch, authState])

    useEffect(() => {
        const loadTeams = async () => {
            const config = await getConfiguration()
            const teamsApi = new TeamsApi(config)
            const teams = await teamsApi.listTeams({})
            const mappedTeams = teams.teams.map(team => ({
                ...team,
                createdAt: team.createdAt.toISOString(),
                updatedAt: team.updatedAt.toISOString(),
            }))
            dispatch(setTeams(mappedTeams))
        }
        loadTeams()
    }, [dispatch])

    useEffect(() => {
        const loadSeasons = async () => {
            const config = await getConfiguration()
            const seasonsApi = new SeasonsApi(config)
            const seasons = await seasonsApi.listSeasons({})
            const mappedSeasons = seasons.seasons.map(season => ({
                ...season,
                beginDate: season.beginDate.toISOString(),
                endDate: season.endDate.toISOString(),
                regularSeasonBeginDate: season.regularSeasonBeginDate.toISOString(),
                regularSeasonEndDate: season.regularSeasonEndDate.toISOString(),
                createdAt: season.createdAt.toISOString(),
                updatedAt: season.updatedAt.toISOString(),
            }))
            dispatch(setSeasons(mappedSeasons))
        }
        loadSeasons()
    }, [dispatch])

    useEffect(() => {
        const loadAccolades = async () => {
            const config = await getConfiguration()
            const accoladesApi = new AccoladesApi(config)
            const accolades = await accoladesApi.listAccolades({})
            const mappedAccolades = accolades.accolades.map(accolade => ({
                ...accolade,
                createdAt: accolade.createdAt.toISOString(),
                updatedAt: accolade.updatedAt.toISOString(),
            }))
            dispatch(setAccolades(mappedAccolades))
        }
        loadAccolades()
    }, [dispatch])

    return (
        <Router>
            <Wrapper>
                <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                        <Route index element={<Home />} />
                        <Route path="/players" element={<PlayersMasterRoute />} />
                        <Route path="/players/:id" element={<PlayersDetailRoute />} />
                        <Route path="/teams" element={<TeamsMasterRoute />} />
                        <Route path="/teams/:id" element={<TeamsDetailRoute />} />
                        <Route path="/matches" element={<MatchesMasterRoute />} />
                        <Route path="/matches/:id" element={<MatchesDetailRoute />} />
                        <Route path="/leagues">
                            <Route index element={<SecureRoute><LeaguesDashboardRoute /></SecureRoute>} />
                            <Route path="create" element={<SecureRoute><CreateLeagueRoute /></SecureRoute>} />
                            <Route path="join" element={<SecureRoute><JoinLeagueRoute /></SecureRoute>} />
                            <Route path="join-code" element={<SecureRoute><JoinLeagueFromCodeRoute /></SecureRoute>} />
                            <Route path=":id" element={<LeagueDetailRoute />}>
                                <Route index element={<LeagueDetailLeaderboardRoute />} />
                                <Route path="picks" element={<LeagueDetailMemberPicksRoute />} />
                                <Route path="picks/:pickID/analytics" element={<PickAnalyticsRoute />} />
                                <Route path="members" element={<LeagueDetailMembersRoute />} />
                                <Route path="bracket-pool">
                                    <Route index element={<LeagueDetailBracketPoolDashboardRoute />} />
                                    <Route path="view/:accountID" element={<LeagueDetailViewBracketRoute />} />
                                    <Route path="create" element={<LeagueDetailModifyBracketRoute />} />
                                </Route>
                                <Route path="admin">
                                    <Route path="settings" element={<LeagueDetailSettingsRoute />} />
                                    <Route path="periods" element={<LeagueDetailPeriodManagementRoute />} />
                                    <Route path="pick-configs" element={<LeagueDetailPickConfigurationsRoute />} />
                                    <Route path="bracket" element={<LeagueDetailBracketManagementRoute />} />
                                    <Route path="members" element={<LeagueDetailMemberManagementRoute />} />
                                    <Route path="bulk-create-match-picks" element={<LeagueDetailBulkCreateMatchPickConfigRoute />} />
                                </Route>
                            </Route>
                            <Route path=":id/pick-config/create" element={<SecureRoute><CreatePickConfigRoute /></SecureRoute>} />
                            <Route path=":id/pick-config/match/:pickConfigID/edit" element={<SecureRoute><EditMatchPickConfigRoute /></SecureRoute>} />
                            <Route path=":id/pick-config/period/:pickConfigID/edit" element={<SecureRoute><EditPeriodPickConfigRoute /></SecureRoute>} />
                        </Route>
                        <Route path="/my-account" element={<SecureRoute><MyAccountRoute /></SecureRoute>} />
                        <Route path="/admin">
                            <Route index element={<SuperadminRoute><AdminPanelRoute /></SuperadminRoute>} />
                            <Route path="tools" element={<SuperadminRoute><AdminToolsRoute /></SuperadminRoute>} />
                            <Route path="brackets">
                                <Route index element={<SuperadminRoute><AdminBracketsMasterRoute /></SuperadminRoute>} />
                                <Route path=":id" element={<SuperadminRoute><AdminBracketDetailRoute /></SuperadminRoute>} />
                            </Route>
                            <Route path="seasons">
                                <Route index element={<SuperadminRoute><AdminSeasonsMasterRoute /></SuperadminRoute>} />
                                <Route path="new" element={<SuperadminRoute><AdminSeasonsDetailRoute /></SuperadminRoute>} />
                                <Route path=":id" element={<SuperadminRoute><AdminSeasonsDetailRoute /></SuperadminRoute>} />
                            </Route>
                            <Route path="teams">
                                <Route index element={<SuperadminRoute><AdminTeamsMasterRoute /></SuperadminRoute>} />
                                <Route path="new" element={<SuperadminRoute><AdminTeamsDetailRoute /></SuperadminRoute>} />
                                <Route path=":id" element={<SuperadminRoute><AdminTeamsDetailRoute /></SuperadminRoute>} />
                                <Route path=":id/seasons/:seasonID" element={<SuperadminRoute><AdminTeamSeasonDetailRoute /></SuperadminRoute>} />
                            </Route>
                            <Route path="players">
                                <Route index element={<SuperadminRoute><AdminPlayersMasterRoute /></SuperadminRoute>} />
                                <Route path="new" element={<SuperadminRoute><AdminPlayersDetailRoute /></SuperadminRoute>} />
                                <Route path=":id" element={<SuperadminRoute><AdminPlayersDetailRoute /></SuperadminRoute>} />
                            </Route>
                            <Route path="matches">
                                <Route index element={<SuperadminRoute><AdminMatchesMasterRoute /></SuperadminRoute>} />
                                <Route path="new" element={<SuperadminRoute><AdminMatchesDetailRoute /></SuperadminRoute>} />
                                <Route path=":id" element={<SuperadminRoute><AdminMatchesDetailRoute /></SuperadminRoute>} />
                            </Route>
                            <Route path="accolades">
                                <Route index element={<SuperadminRoute><AdminAccoladesMasterRoute /></SuperadminRoute>} />
                            </Route>
                            <Route path="accounts" element={<SuperadminRoute><AdminAccountsMasterRoute /></SuperadminRoute>} />
                        </Route>
                        <Route path="/email-unsubscribe" element={<EmailUnsubscribe />} />
                        <Route path='/attendance' element={<AttendanceRoute />} />
                        <Route path="/auth" element={<NoAuthRoute />}>
                            <Route path="sign-in" element={<SignIn />} />
                            <Route path="login-callback" element={<LoginCallback />} />
                            <Route path="forgot-password" element={<ForgotPassword />} />
                            <Route path="reset-password" element={<ResetPassword />} />
                            <Route path="create-account" element={<CreateAccount />} />
                            <Route path="confirm" element={<ConfirmAccount />} />
                        </Route>
                    </Routes>
                </Suspense>
            </Wrapper>
        </Router>
    )
}